import { useRef, useState, type ReactNode } from "react";
import { ConfirmDialog } from "../feedback/ConfirmDialog";
import { useHotkeys } from "../hotkeys";
import { ContentHeader } from "../shell/ContentHeader";

type Props = {
  title: string;
  breadcrumb?: string[];
  onSave(): void;
  onCancel(): void;
  saving?: boolean;
  /** Com alterações não salvas, Esc e Cancelar pedem confirmação (FE23). */
  dirty?: boolean;
  children: ReactNode;
  aside?: ReactNode;
};

export function FormPage({
  title,
  breadcrumb,
  onSave,
  onCancel,
  saving,
  dirty = false,
  children,
  aside,
}: Props) {
  const formRef = useRef<HTMLFormElement>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const cancel = () => (dirty ? setConfirmDiscard(true) : onCancel());
  useHotkeys(
    {
      "alt+s": (e) => {
        if (e.repeat || saving) return;
        // MoneyInput, QtyInput e DocInput confirmam o valor no blur: tira o foco e submete na próxima frame,
        // quando o onSave já foi recriado com os valores novos (FE7).
        (document.activeElement as HTMLElement | null)?.blur();
        requestAnimationFrame(() => formRef.current?.requestSubmit());
      },
      escape: () => cancel(),
    },
    { allowInInputs: ["alt+s", "escape"] },
  );
  return (
    <form
      ref={formRef}
      onSubmit={(e) => {
        e.preventDefault();
        if (!saving) onSave();
      }}
      noValidate
    >
      <ContentHeader
        title={title}
        breadcrumb={breadcrumb}
        actions={
          <>
            <button type="button" className="btn btn-outline-secondary" onClick={cancel}>
              Cancelar <kbd className="ms-1 d-none d-lg-inline">Esc</kbd>
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Salvando…" : "Salvar"} <kbd className="ms-1 d-none d-lg-inline">Alt+S</kbd>
            </button>
          </>
        }
      />
      <p className="text-muted small">(*) Campos obrigatórios</p>
      <div className="row">
        <div className={aside ? "col-xl-9" : "col-12"}>{children}</div>
        {aside && <aside className="col-xl-3">{aside}</aside>}
      </div>
      {confirmDiscard && (
        <ConfirmDialog
          open
          title="Descartar alterações?"
          message="As alterações não salvas serão perdidas."
          confirmLabel="Descartar"
          cancelLabel="Continuar editando"
          onConfirm={() => {
            setConfirmDiscard(false);
            onCancel();
          }}
          onCancel={() => setConfirmDiscard(false)}
        />
      )}
    </form>
  );
}
